import { useState } from 'react';
import claudeLogo from '~/deck2/assets/claude.svg';
import openaiLogo from '~/deck2/assets/openai.svg';
import blush from './assets/silhouette-blush.webp';
import lavender from './assets/silhouette-lavender.webp';
import rose from './assets/silhouette-rose.webp';
import sage from './assets/silhouette-sage.webp';
import tan from './assets/silhouette-tan.webp';
import teal from './assets/silhouette-teal.webp';
import { LAUNCH, start } from './config';
import styles from './landing.module.css';

/**
 * The brand silhouette nearest their company's colour (Paper "Deck branding", Backgrounds): only our own
 * colours ever show, but a Stripe designer lands on lavender and a Duolingo one on sage. Teal, the brand
 * moment, when there is no company or its logo has no colour.
 */
export function silhouetteFor(colour: string | null): string {
  const rgb = colour?.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i)?.slice(1).map((v) => parseInt(v, 16) / 255);
  if (!rgb) return teal;
  const [r, g, b] = rgb;
  const max = Math.max(r, g, b);
  const chroma = max - Math.min(r, g, b);
  if (chroma < 0.15) return teal;
  const hue = 60 * (max === r ? ((g - b) / chroma + 6) % 6 : max === g ? (b - r) / chroma + 2 : (r - g) / chroma + 4);
  if (hue < 15 || hue >= 345) return rose;
  if (hue < 70) return tan;
  if (hue < 165) return sage;
  if (hue < 220) return teal;
  if (hue < 290) return lavender;
  return blush;
}

/** The opening day as an all-day calendar event, as a file Apple Calendar, Outlook and Google all take. */
function calendarFile() {
  const day = LAUNCH.day.replace(/-/g, '');
  const next = new Date(`${LAUNCH.day}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  const after = next.toISOString().slice(0, 10).replace(/-/g, '');
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Deck//Opening day//EN',
    'BEGIN:VEVENT',
    `UID:deck-opens-${day}@usedeck.ai`,
    `DTSTAMP:${day}T000000Z`,
    `DTSTART;VALUE=DATE:${day}`,
    `DTEND;VALUE=DATE:${after}`,
    `SUMMARY:${start.done.event}`,
    'DESCRIPTION:Deck opens today. Start my case study at https://usedeck.ai',
    'URL:https://usedeck.ai',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

function GeminiStar() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden>
      <defs>
        <linearGradient id="gemini-star" x1="4" y1="20" x2="20" y2="4" gradientUnits="userSpaceOnUse">
          <stop stopColor="#4285F4" />
          <stop offset="0.55" stopColor="#9B72CB" />
          <stop offset="1" stopColor="#D96570" />
        </linearGradient>
      </defs>
      <path d="M12 2c.6 5.3 4.7 9.4 10 10-5.3.6-9.4 4.7-10 10-.6-5.3-4.7-9.4-10-10 5.3-.6 9.4-4.7 10-10Z" fill="url(#gemini-star)" />
    </svg>
  );
}

/**
 * The last screen: the day they get in, which adds itself to their calendar when pressed, and one message
 * they save in their own AI so it remembers Deck and reminds them on the day. They see every word before they send it. Each button also copies it, for an
 * assistant that doesn't take a message in its link.
 */
export function LaunchStep({ message }: { message: string }) {
  const [copied, setCopied] = useState(false);
  const asked = encodeURIComponent(message);

  const copy = () => {
    void navigator.clipboard?.writeText(message).then(
      () => setCopied(true),
      () => undefined,
    );
  };

  return (
    <section className={styles.launch}>
      <div className={styles.launchBody}>
        <h1 className={styles.launchTitle}>
          {start.done.title}{' '}
          <a className={styles.launchDay} href={calendarFile()} download="deck-opens.ics" title={start.done.addToCalendar} aria-label={start.done.addToCalendar}>
            {LAUNCH.weekday}
          </a>
          .
        </h1>
        <p className={styles.launchLede}>{start.done.lede}</p>
        <div className={styles.launchActions}>
          <a className={`${styles.button} ${styles.ink} ${styles.medium}`} href={`https://claude.ai/new?q=${asked}`} target="_blank" rel="noopener noreferrer" onClick={copy}>
            <img src={claudeLogo} alt="" />
            {start.done.claude}
          </a>
          <a className={`${styles.button} ${styles.white} ${styles.medium}`} href={`https://chatgpt.com/?q=${asked}`} target="_blank" rel="noopener noreferrer" onClick={copy}>
            <img src={openaiLogo} alt="" />
            {start.done.chatgpt}
          </a>
          <a className={`${styles.button} ${styles.white} ${styles.medium}`} href="https://gemini.google.com/app" target="_blank" rel="noopener noreferrer" onClick={copy}>
            <GeminiStar />
            {start.done.gemini}
          </a>
          <button type="button" className={`${styles.button} ${styles.white} ${styles.medium}`} onClick={copy} aria-live="polite">
            {copied ? start.done.copied : start.done.copy}
          </button>
        </div>
      </div>
    </section>
  );
}
